import { VALIDATION_ERRORS } from '@/lib/units/unit-conversion'

/** The outcome of validating one keystroke's worth of text in a Leading/Kerning/Tracking input. */
export type SpacingInputResult =
  | { kind: 'blank'; message: string }
  /** A lone '-' while the user starts typing a negative number: no error, nothing to apply. */
  | { kind: 'partial' }
  | { kind: 'invalid'; message: string }
  | { kind: 'range'; message: string }
  | { kind: 'valid'; value: number }

interface SpacingInputRules {
  min: number
  max: number
  rangeMessage: string
  /** Kerning/Tracking: allow a lone '-' and reject any character outside a signed decimal.
   *  Leading leaves both off and relies on `parseFloat` alone. */
  strict: boolean
}

/**
 * Validates a numeric spacing input as it is typed. Required-field validation shows as soon as the
 * field is emptied, not only once the user blurs — the committed value is never touched by a blank
 * or invalid keystroke, and blur reverts the display.
 */
export function validateSpacingInput(
  raw: string,
  { min, max, rangeMessage, strict }: SpacingInputRules
): SpacingInputResult {
  if (raw === '') return { kind: 'blank', message: VALIDATION_ERRORS.CANT_BE_BLANK }

  if (strict) {
    // Allow '-' alone while the user starts typing a negative number
    if (raw === '-') return { kind: 'partial' }

    // Reject invalid characters: only digits, optional leading minus, optional decimal dot
    if (!/^-?\d*\.?\d*$/.test(raw)) return { kind: 'invalid', message: 'Invalid value' }
  }

  // Strip trailing decimal for validation only
  const n = parseFloat(raw.endsWith('.') ? `${raw}0` : raw)

  if (!isFinite(n)) return { kind: 'invalid', message: 'Invalid value' }

  if (n < min || n > max) return { kind: 'range', message: rangeMessage }

  return { kind: 'valid', value: n }
}

/** Kerning/Tracking accept up to 3 whole digits and 2 decimals, plus an optional minus sign —
 *  the legacy editor's limit ("96.55", "-200.55"). */
const SPACING_MAX_WHOLE_DIGITS = 3
const SPACING_MAX_DECIMALS = 2

/** True when `raw` has more whole or decimal digits than a Kerning/Tracking input allows. Checked on
 *  every change so the limit holds for typing, pasting and editing alike (a static `maxLength`
 *  can't follow the sign or the decimal point). */
export function exceedsSpacingInputLimit(raw: string): boolean {
  const [whole, decimals = ''] = raw.replace(/^-/, '').split('.')

  return whole.length > SPACING_MAX_WHOLE_DIGITS || decimals.length > SPACING_MAX_DECIMALS
}

/**
 * The value to apply when a spacing input is committed (Enter or blur), or `null` to revert to the
 * last valid value. Only the complete input is validated here — intermediate keystrokes (e.g. "-15"
 * on the way to "-150") are never applied, so a revert can't land on a partially typed value.
 */
export function resolveSpacingCommit(raw: string, rules: SpacingInputRules): number | null {
  const result = validateSpacingInput(raw, rules)

  return result.kind === 'valid' ? result.value : null
}

/**
 * What a spacing slider announces instead of a bare number: the value rounded to 2 decimals with its
 * unit ("57.6 points", never "57.599998474121094"), or "Mixed"/"Unavailable" for those states.
 */
export function spacingSliderValueText(
  value: number,
  unit: string,
  { mixed, unavailable }: { mixed: boolean; unavailable: boolean }
): string {
  if (mixed) return 'Mixed'
  if (unavailable) return 'Unavailable'

  return `${Number(value.toFixed(2))} ${unit}`
}

/** A blank spacing input shows "Mixed" for a mixed selection and a greyed '-' otherwise. */
export function resolveSpacingPlaceholder(inputValue: string, mixed: boolean): string | undefined {
  return inputValue === '' ? (mixed ? 'Mixed' : '-') : undefined
}
